import env from './env';

// These errors belong to the desktop handoff flow, rather than the global
// HTTP handler that logs users out and redirects them on a 401.
export class DesktopSamlLoginError extends Error {
  constructor() {
    super('Desktop SAML login could not be completed');
    this.name = 'DesktopSamlLoginError';
  }
}

export const isDesktopSamlCallback = () =>
  window.location.pathname.replace(/\/$/, '') === '/saml2/login' &&
  new URLSearchParams(window.location.search)
    .getAll('source')
    .includes('desktop');

export const getDesktopSamlCallback = () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('handoff_code');
  if (
    params.getAll('source').length !== 1 ||
    params.get('source') !== 'desktop' ||
    params.getAll('handoff_code').length !== 1 ||
    !code ||
    code.length > 128 ||
    !/^[A-Za-z0-9_-]+$/.test(code)
  ) {
    throw new DesktopSamlLoginError();
  }
  // Forward only the known callback on this frontend origin, never an
  // arbitrary next_path, redirect, or token-bearing extra query parameter.
  const callback = new URL('/saml2/login', window.location.origin);
  callback.searchParams.set('source', 'desktop');
  callback.searchParams.set('handoff_code', code);
  return {
    code,
    launchUrl: `avoma-app://login?passthrough=${encodeURIComponent(
      callback.href
    )}`
  };
};

export const fetchDesktopSamlDetails = async (
  email?: string,
  issuerUrl?: string
) => {
  try {
    const response = await fetch(
      `${env.avomaApiUrl}/api/v1/avomalogins/sso_details/`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          issuer_url: issuerUrl,
          source: 'desktop'
        })
      }
    );
    if (!response.ok) throw new DesktopSamlLoginError();
    return await response.json();
  } catch {
    throw new DesktopSamlLoginError();
  }
};

export const redeemDesktopSamlCode = async (code: string) => {
  if (!code || code.length > 128) throw new DesktopSamlLoginError();
  try {
    const response = await fetch(
      `${env.avomaApiUrl}/api/v1/avomalogins/login_handoff/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      }
    );
    if (!response.ok) throw new DesktopSamlLoginError();
    return await response.json();
  } catch {
    // Do not expose response bodies, the redeemable code, or network URLs.
    throw new DesktopSamlLoginError();
  }
};

// Retain the complete action promise (including failures) for this document.
// A remount must not redeem twice or repeat LOGIN_SUCCESS / login analytics.
// A fresh login/full navigation starts a new document and a new code.
const handoffActions = new Map<string, Promise<unknown>>();
export function runDesktopSamlLoginOnce<T>(
  code: string,
  login: () => Promise<T>
): Promise<T> {
  const existing = handoffActions.get(code);
  if (existing) return existing as Promise<T>;
  // A callback document normally handles one code. Fail closed rather than
  // retaining an unbounded number or evicting a code that could be replayed.
  if (handoffActions.size >= 32)
    return Promise.reject(new DesktopSamlLoginError());
  const action = Promise.resolve().then(login);
  handoffActions.set(code, action);
  return action;
}

const analyticsStorageKey = 'avomaDesktopSamlAnalytics';
type DesktopSamlAnalytics = {
  username: string;
  accountCreated: boolean;
  orgId?: string;
  orgCreatedAt?: string;
};

// Persist only authentication-event metadata across the callback's full reload.
// Never persist the handoff code, access token, response, or callback URL.
export const deferDesktopSamlAnalytics = (metadata: DesktopSamlAnalytics) => {
  try {
    sessionStorage.setItem(
      analyticsStorageKey,
      JSON.stringify({
        username: metadata.username,
        accountCreated: metadata.accountCreated,
        orgId: metadata.orgId,
        orgCreatedAt: metadata.orgCreatedAt,
        createdAt: Date.now()
      })
    );
  } catch {
    // Telemetry storage must never fail an otherwise successful login.
  }
};

export const consumeDesktopSamlAnalytics = (
  username?: string
): DesktopSamlAnalytics | null => {
  try {
    const raw = sessionStorage.getItem(analyticsStorageKey);
    if (!raw) return null;
    sessionStorage.removeItem(analyticsStorageKey);
    const data = JSON.parse(raw);
    const age = Date.now() - data.createdAt;
    if (
      !username ||
      data.username !== username ||
      typeof data.accountCreated !== 'boolean' ||
      typeof data.createdAt !== 'number' ||
      !Number.isFinite(age) ||
      age < 0 ||
      age > 300000 ||
      (data.orgId !== undefined && typeof data.orgId !== 'string') ||
      (data.orgCreatedAt !== undefined && typeof data.orgCreatedAt !== 'string')
    )
      return null;
    return {
      username,
      accountCreated: data.accountCreated,
      orgId: data.orgId,
      orgCreatedAt: data.orgCreatedAt
    };
  } catch {
    return null;
  }
};

export const scheduleDesktopSamlAnalytics = (deliver: () => void) => {
  const safelyDeliver = () => {
    try {
      deliver();
    } catch {
      /* Tracking must not interrupt desktop landing. */
    }
  };
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', safelyDeliver, { once: true });
  } else {
    window.setTimeout(safelyDeliver, 0);
  }
};
